import { useInstantSearch } from 'react-instantsearch';
import { defineMessages } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import {
  LeftFilters,
  SearchLayoutContainer,
  SearchLayoutMain,
} from '@sorare/core/src/components/search/SearchLayout/ui';
import { AlgoliaCardIndexesNames } from '@sorare/core/src/contexts/config';
import Highlightable from '@sorare/core/src/contexts/highlight/Highlightable';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useToggle from '@sorare/core/src/hooks/useToggle';
import useShowBottomBarNav from '@sorare/core/src/routing/MultiSportBottomNavBar/useShowBottomBarNav';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ActiveFilters } from 'search/ActiveFilters';
import { SavedFilters } from 'search/SavedFilters';
import CardResultsFromGraphQL from 'searchCards/CardResultsFromGraphQL';
import { EarlyAccessInfos } from 'searchCards/EarlyAccessInfos';
import StackedSwitch from 'searchCards/StackedSwitch';
import { TotalResults } from 'searchCards/TotalResults';

import FiltersManager from '../../search/FiltersManager';
import FiltersManagerDialog from '../../search/FiltersManagerDialog';
import SearchBox from '../../search/SearchBox';
import ToggleFiltersManager from '../../search/ToggleFiltersManager';
import { Segment } from '../Segment';
import SortCards from '../SortCards';
import { Props } from './types';

export const messages = defineMessages({
  defaultPlaceholder: {
    id: 'AdvancedCardSearch.placeholder',
    defaultMessage: 'Filter by player, team or rarity',
  },
  playerPlaceholder: {
    id: 'AdvancedCardSearch.playerPlaceholder',
    defaultMessage: 'Filter cards of {name} by rarity or team',
  },
  teamPlaceholder: {
    id: 'AdvancedCardSearch.teamPlaceholder',
    defaultMessage: 'Filter cards of {name} by player or rarity',
  },
  leaguePlaceholder: {
    id: 'AdvancedCardSearch.leaguePlaceholder',
    defaultMessage: 'Filter cards of {name} by player, team or rarity',
  },
  countryPlaceholder: {
    id: 'AdvancedCardSearch.countryPlaceholder',
    defaultMessage: 'Filters cards of {name} by player, team or rarity',
  },
  stackPlaceholder: {
    id: 'AdvancedCardSearch.stackPlaceholder',
    defaultMessage: 'Filters cards of {name}',
  },
  favoriteCardsPlaceholder: {
    id: 'AdvancedCardSearch.favoriteCardsPlaceholder',
    defaultMessage: 'Filters favorite cards by player, team or rarity',
  },
  instantBuy: {
    id: 'AdvancedCardSearch.instantBuy',
    defaultMessage: 'Filter by player or team',
  },
});

const NoSmoothScrollStyle = createGlobalStyle`
  html {
    /* overrides the default scroll behavior we have in global style.css */
    scroll-behavior: initial;
  }
`;

const Root = styled(Vertical).attrs({ gap: 3 })``;

const Cards = styled.div`
  position: relative;
  border-radius: 8px;
`;

const SortAndSegment = styled(Vertical).attrs({ gap: 1.5 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
const SortCtn = styled(Horizontal)`
  width: 100%;
  padding-bottom: var(--intermediate-unit);
  border-bottom: 1px solid rgba(var(--c-rgb-neutral-1000), 0.11);
  @media ${laptopAndAbove} {
    width: auto;
    padding-bottom: 0;
    border-bottom: none;
  }
`;
const SearchCtn = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 0 var(--double-unit);
`;

const SearchRow = styled(Horizontal).attrs({ gap: 1.5 })`
  justify-content: space-between;
`;

const ToggleFiltersPlaceholder = styled.div`
  height: var(--quadruple-unit);
`;

const ToggleFiltersWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  position: fixed;
  z-index: 2;
  bottom: var(--triple-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  justify-content: stretch;
`;

const VerticalScroll = styled.div`
  overflow: auto;
  flex-shrink: 0;
  height: 100%;
  position: sticky;
  top: var(--current-stack-height);
  max-height: calc(100vh - var(--current-stack-height));
  padding-right: var(--triple-unit);
`;

export const SearchLayout = (props: Props) => {
  const { up: isDesktopOrAbove } = useScreenSize('desktop');
  const {
    topic,
    stackableTypes,
    favPlayerHit,
    hideSavedFilters = false,
    hideStackedSwitch = false,
    alwaysShowFavoriteButton = false,
    editableLists = false,
    cardFilters,
    advancedCardFilters,
    sorts = [
      'Newly Listed',
      'Ending Soon',
      'Highest Price',
      'Lowest Price',
    ] as AlgoliaCardIndexesNames,
    children,
    hideOwner = false,
    galleryOwnerSlug,
    filtersTour = false,
    cardsTour = false,
    removeFinishedAuctions = false,
    removeEndedSingleSaleOffers = false,
    removeFinishedPrimaryBuys = false,
    banner,
    hideSorareUser,
    onlyPrimary,
    PlayerDetailsComponent,
    sport,
    GridComponent = CardResultsFromGraphQL,
    canSellCommon,
  } = props;
  const { up: isLaptop } = useScreenSize('laptop');

  const bottomNavBar = useShowBottomBarNav();
  const [filtersOpen, toggleFilters] = useToggle(false);
  const { formatMessage } = useIntlContext();
  const { refresh } = useInstantSearch();

  const responsiveFilters = !isLaptop;

  const searchBoxPlaceholder = topic
    ? {
        player: messages.playerPlaceholder,
        team: messages.teamPlaceholder,
        league: messages.leaguePlaceholder,
        country: messages.countryPlaceholder,
        stack: messages.stackPlaceholder,
        favoriteCards: messages.favoriteCardsPlaceholder,
        instantBuy: messages.instantBuy,
      }[topic.type]
    : messages.defaultPlaceholder;

  const searchLayout = (
    <Root>
      <NoSmoothScrollStyle />
      {banner}
      <SearchLayoutContainer>
        {!responsiveFilters && (
          <VerticalScroll>
            <Highlightable {...filtersTour} disabled={!filtersTour}>
              <LeftFilters visible>
                <SearchCtn>
                  <SearchBox
                    withClearIcon
                    placeholder={formatMessage(searchBoxPlaceholder, {
                      name: topic?.label,
                    })}
                    favPlayerHit={favPlayerHit}
                  />
                  <SearchRow>{!hideSavedFilters && <SavedFilters />}</SearchRow>
                  <SearchRow>
                    <TotalResults />
                  </SearchRow>
                </SearchCtn>
                <FiltersManager
                  filters={cardFilters}
                  advancedFilters={advancedCardFilters}
                  skipClearAllButton
                />
              </LeftFilters>
            </Highlightable>
          </VerticalScroll>
        )}
        <SearchLayoutMain>
          <SortAndSegment>
            <SortCtn>
              {!isLaptop && (
                <SearchBox
                  withClearIcon
                  placeholder={formatMessage(searchBoxPlaceholder, {
                    name: topic?.label,
                  })}
                  favPlayerHit={favPlayerHit}
                />
              )}
              {stackableTypes?.length && !hideStackedSwitch && (
                <StackedSwitch />
              )}
              {isLaptop && <SortCards indexes={sorts} />}
              {!isLaptop && !hideSavedFilters && <SavedFilters />}
            </SortCtn>
            <Segment />
          </SortAndSegment>
          <EarlyAccessInfos />
          {children}
          <ActiveFilters />
          <Highlightable {...cardsTour} position="left" disabled={!cardsTour}>
            <Cards>
              <GridComponent
                hideOwner={hideOwner}
                galleryOwnerSlug={galleryOwnerSlug}
                removeFinishedAuctions={removeFinishedAuctions}
                removeEndedSingleSaleOffers={removeEndedSingleSaleOffers}
                removeFinishedPrimaryBuys={removeFinishedPrimaryBuys}
                topic={topic}
                hideSorareUser={hideSorareUser}
                stackableTypes={stackableTypes}
                showDesktopFilter
                alwaysShowFavoriteButton={alwaysShowFavoriteButton}
                editableLists={editableLists}
                onlyPrimary={onlyPrimary}
                PlayerDetailsComponent={PlayerDetailsComponent}
                sport={sport}
                canSellCommon={canSellCommon}
              />
            </Cards>
          </Highlightable>
        </SearchLayoutMain>
      </SearchLayoutContainer>
      {responsiveFilters && (
        <>
          {bottomNavBar ? (
            <Portal id="above-bottom-bar-portal">
              <Highlightable {...filtersTour} disabled={!filtersTour}>
                <ToggleFiltersManager toggleFilters={toggleFilters} />
              </Highlightable>
            </Portal>
          ) : (
            <ToggleFiltersPlaceholder>
              <ToggleFiltersWrapper>
                <Highlightable {...filtersTour} disabled={!filtersTour}>
                  <ToggleFiltersManager toggleFilters={toggleFilters} />
                </Highlightable>
              </ToggleFiltersWrapper>
            </ToggleFiltersPlaceholder>
          )}
          <FiltersManagerDialog
            open={filtersOpen}
            onClose={toggleFilters}
            filtersGroups={cardFilters}
            sorts={sorts}
          />
        </>
      )}
    </Root>
  );
  if (isDesktopOrAbove) return searchLayout;
  return (
    <PullToRefetch refetch={async () => refresh()}>
      {searchLayout}
    </PullToRefetch>
  );
};

export default SearchLayout;
